import React, { useEffect, useState } from 'react';
import { supabase } from '@/integrations/supabase/client';
import { useSupabaseAuth } from '@/context/SupabaseAuthContext';

function getMonthRange(date) {
  const start = new Date(date.getFullYear(), date.getMonth(), 1);
  const end = new Date(date.getFullYear(), date.getMonth() + 1, 0);
  return [start.toISOString().slice(0, 10), end.toISOString().slice(0, 10)];
}

const CATEGORY_LABELS = {
  food: 'Food & Drink',
  transport: 'Travel',
  goods: 'Goods & Services',
  utilities: 'Utilities',
};

const CarbonDrilldownModal: React.FC<{
  open: boolean;
  onClose: () => void;
  category: string;
  month: Date;
}> = ({ open, onClose, category, month }) => {
  const { user } = useSupabaseAuth();
  const [logs, setLogs] = useState<any[]>([]);
  const [loading, setLoading] = useState(true);

  useEffect(() => {
    if (!open || !user) return;
    setLoading(true);
    const [start, end] = getMonthRange(month);
    supabase
      .from('carbon_logs')
      .select('*')
      .eq('user_id', user.id)
      .gte('date', start)
      .lte('date', end)
      .then(({ data }) => {
        console.log('Fetched carbon_logs:', data);
        setLogs(data || []);
        setLoading(false);
      });
  }, [open, user, month]);

  if (!open) return null;

  let entries: any[] = [];
  if (category === 'food') {
    entries = logs.flatMap(log => (log.food || []).map(f => ({ ...f, date: log.date })));
  } else if (category === 'transport') {
    entries = logs.flatMap(log => (log.transport || []).map(t => ({ ...t, date: log.date })));
  } else if (category === 'utilities') {
    entries = logs.map(log => ({ ...log.home, date: log.date }));
  } else if (category === 'goods') {
    // Show goods data: amount spent and estimated CO₂
    entries = logs
      .filter(log => log.goods && parseFloat(log.goods) > 0)
      .map(log => ({
        amount: parseFloat(log.goods),
        estimatedCO2: parseFloat(log.goods) * 0.5,
        date: log.date
      }));
  }

  return (
    <div className="fixed inset-0 z-50 flex items-center justify-center bg-black/60">
      <div className="bg-white dark:bg-[#23272a] rounded-2xl p-6 shadow-2xl w-full max-w-lg relative">
        <button
          className="absolute top-4 right-4 text-black dark:text-white text-xl"
          onClick={onClose}
          aria-label="Close"
        >
          ×
        </button>
        <h3 className="text-xl font-bold mb-4 text-[#40e0d0]">{CATEGORY_LABELS[category] || category} Details</h3>
        {loading ? (
          <div className="text-center text-muted-foreground">Loading...</div>
        ) : entries.length === 0 ? (
          <div className="text-center text-muted-foreground">No data for this category this month.</div>
        ) : (
          <div className="max-h-80 overflow-y-auto">
            <ul className="divide-y divide-gray-200 dark:divide-gray-700">
              {entries.map((entry, idx) => (
                <li key={idx} className="py-2 flex flex-col sm:flex-row sm:items-center sm:justify-between">
                  <span className="text-sm text-gray-700 dark:text-gray-200">
                    {category === 'food' && (
                      <>
                        <span className="font-bold capitalize">{entry.type}</span> — {entry.servings} servings
                      </>
                    )}
                    {category === 'transport' && (
                      <>
                        <span className="font-bold capitalize">{entry.mode}</span> — {entry.distance} km
                      </>
                    )}
                    {category === 'utilities' && (
                      <>
                        Electricity: {entry.electricity || 0} kWh, Gas: {entry.gas || 0} kWh, Water: {entry.water || 0} L
                      </>
                    )}
                    {category === 'goods' && (
                      <>
                        <span className="font-bold">${entry.amount.toFixed(2)}</span> spent — <span className="font-bold">{entry.estimatedCO2.toFixed(1)} kg CO₂e</span>
                      </>
                    )}
                  </span>
                  <span className="text-xs text-muted-foreground mt-1 sm:mt-0">{entry.date}</span>
                </li>
              ))}
            </ul>
          </div>
        )}
      </div>
    </div>
  );
};

export default CarbonDrilldownModal; 